import React from 'react';
import { motion } from 'framer-motion';
import { useScrollReveal } from '../../hooks/useScrollAnimations';
import { ArrowRight, MessageSquare, Image, Lightbulb, Layers, Sparkles, Network, Target } from 'lucide-react';

const AnalysisSection: React.FC = () => {
  const { ref: titleRef, isInView: titleInView } = useScrollReveal(0.3);
  const { ref: aiFlowRef, isInView: aiFlowInView } = useScrollReveal(0.2);
  const { ref: stickerFlowRef, isInView: stickerFlowInView } = useScrollReveal(0.2);

  return (
    <section className="tmf-analysis green-accent">
      <div className="tmf-container">
        {/* Section Title */}
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 50 }}
          animate={titleInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="tmf-section-header"
        >
          <h2 className="tmf-section-title">Analysis Process</h2>
          <p className="tmf-section-intro">
            We used collaborative coding methods to ensure rigor and reduce individual bias.
            Each data source required a different analytical approach to honor its depth and context.
          </p>
        </motion.div>

        {/* Data Sources */}
        <motion.div
          ref={aiFlowRef}
          initial={{ opacity: 0, y: 50 }}
          animate={aiFlowInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="tmf-analysis-unified"
        >
          <h3 className="tmf-analysis-stage-title">Data Collection & Coding</h3>

          <div className="tmf-analysis-dual-track">
            {/* AI Interview Track */}
            <div className="tmf-analysis-track">
              <div className="tmf-analysis-track-header">
                <div className="tmf-analysis-track-icon">
                  <MessageSquare size={28} />
                </div>
                <h4 className="tmf-analysis-track-title">AI + Insight Box Interview Transcripts</h4>
                <p className="tmf-analysis-track-subtitle">26 transcripts</p>
              </div>

              <div className="tmf-analysis-track-steps">
                <div className="tmf-analysis-track-step">
                  <div className="tmf-analysis-track-step-marker">01</div>
                  <div className="tmf-analysis-track-step-content">
                    <h5>Primary Coding</h5>
                    <p>Each member codes 5 transcripts</p>
                    <span className="tmf-analysis-mini-badge">Dedoose</span>
                  </div>
                </div>

                <div className="tmf-analysis-track-step">
                  <div className="tmf-analysis-track-step-marker">02</div>
                  <div className="tmf-analysis-track-step-content">
                    <h5>Secondary Coding</h5>
                    <p>Swap & code 5 more for reliability</p>
                    <span className="tmf-analysis-mini-badge">Intercoder Reliability</span>
                  </div>
                </div>
              </div>

              <div className="tmf-analysis-insight-box">
                <div className="tmf-analysis-insight-icon">
                  <Lightbulb size={18} />
                </div>
                <div className="tmf-analysis-insight-content">
                  <p className="tmf-analysis-insight-text">
                    We conducted 3 insight box sessions, each 1 hour long, generating detailed transcripts that revealed deep insights into user mobility patterns and access barriers.
                  </p>
                </div>
              </div>
            </div>

            {/* Sticker Wall Track */}
            <div className="tmf-analysis-track">
              <div className="tmf-analysis-track-header">
                <div className="tmf-analysis-track-icon">
                  <Image size={28} />
                </div>
                <h4 className="tmf-analysis-track-title">Sticker Wall</h4>
                <p className="tmf-analysis-track-subtitle">Visual data</p>
              </div>

              <div className="tmf-analysis-track-steps">
                <div className="tmf-analysis-track-step">
                  <div className="tmf-analysis-track-step-marker">01</div>
                  <div className="tmf-analysis-track-step-content">
                    <h5>Photo Documentation</h5>
                    <p>Captured filled sticker wall</p>
                  </div>
                </div>

                <div className="tmf-analysis-track-step">
                  <div className="tmf-analysis-track-step-marker">02</div>
                  <div className="tmf-analysis-track-step-content">
                    <h5>Digital Coding & Memos</h5>
                    <p>Coded in Dedoose with detailed notes</p>
                    <span className="tmf-analysis-mini-badge">Dedoose</span>
                  </div>
                </div>

                <div className="tmf-analysis-track-step">
                  <div className="tmf-analysis-track-step-marker">03</div>
                  <div className="tmf-analysis-track-step-content">
                    <h5>Team Discussion</h5>
                    <p>Grounded assumptions in facts</p>
                    <span className="tmf-analysis-mini-badge">Grounded Analysis</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            className="tmf-analysis-convergence"
            initial={{ opacity: 0, y: 20 }}
            animate={aiFlowInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div
              className="tmf-analysis-convergence-arrow"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={aiFlowInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <svg width="100%" height="90" viewBox="0 0 400 90">
                {/* Top path - AI + Insight Box */}
                <motion.path
                  d="M 50 15 Q 200 45 350 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={aiFlowInView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{ duration: 1.5, delay: 0.8, ease: "easeInOut" }}
                />
                {/* Middle path - Sticker Wall */}
                <motion.path
                  d="M 50 45 L 350 45"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={aiFlowInView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{ duration: 1.5, delay: 1, ease: "easeInOut" }}
                />
                {/* Bottom path - Third source */}
                <motion.path
                  d="M 50 75 Q 200 45 350 75"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={aiFlowInView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{ duration: 1.5, delay: 1.2, ease: "easeInOut" }}
                />
                {/* Center convergence point */}
                <motion.circle
                  cx="200"
                  cy="45"
                  r="6"
                  fill="currentColor"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={aiFlowInView ? {
                    scale: [0, 1.2, 1],
                    opacity: 1
                  } : {}}
                  transition={{
                    duration: 0.6,
                    delay: 2.5,
                    ease: "easeOut"
                  }}
                />
                {/* Pulsing ring around center point */}
                <motion.circle
                  cx="200"
                  cy="45"
                  r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={aiFlowInView ? {
                    scale: [0.5, 1.5, 1.5],
                    opacity: [0, 0.6, 0]
                  } : {}}
                  transition={{
                    duration: 2,
                    delay: 2.5,
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                />
              </svg>
            </motion.div>
            <motion.p
              className="tmf-analysis-convergence-text"
              initial={{ opacity: 0, y: 10 }}
              animate={aiFlowInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 2.8 }}
            >
              All three data sources feed into unified thematic analysis
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Unified Thematic Analysis Process */}
        <motion.div
          ref={stickerFlowRef}
          initial={{ opacity: 0, y: 50 }}
          animate={stickerFlowInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="tmf-analysis-method"
        >
          <h3 className="tmf-analysis-stage-title">Thematic Analysis Process</h3>

          {/* FigJam Video */}
          <motion.div
            className="tmf-analysis-video-container"
            initial={{ opacity: 0, y: 30 }}
            animate={stickerFlowInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <video
              className="tmf-analysis-video"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src="https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/tmf-figjam.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>

          <div className="tmf-analysis-flow">
            {/* Step 1: Code Grouping */}
            <motion.div
              className="tmf-analysis-step"
              initial={{ opacity: 0, x: -30 }}
              animate={stickerFlowInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="tmf-analysis-step-number">01</div>
              <div className="tmf-analysis-step-content">
                <div className="tmf-analysis-step-icon">
                  <Layers size={24} />
                </div>
                <h4 className="tmf-analysis-step-title">Code Grouping</h4>
                <p className="tmf-analysis-step-description">
                  Organize similar codes into meaningful categories
                </p>
              </div>
            </motion.div>

            <motion.div
              className="tmf-analysis-arrow"
              initial={{ opacity: 0, scale: 0 }}
              animate={stickerFlowInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <ArrowRight size={24} />
            </motion.div>

            {/* Step 2: Identify Themes */}
            <motion.div
              className="tmf-analysis-step"
              initial={{ opacity: 0, x: -30 }}
              animate={stickerFlowInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="tmf-analysis-step-number">02</div>
              <div className="tmf-analysis-step-content">
                <div className="tmf-analysis-step-icon">
                  <Sparkles size={24} />
                </div>
                <h4 className="tmf-analysis-step-title">Identify Themes</h4>
                <p className="tmf-analysis-step-description">
                  Extract key themes emerging from grouped codes
                </p>
              </div>
            </motion.div>

            <motion.div
              className="tmf-analysis-arrow"
              initial={{ opacity: 0, scale: 0 }}
              animate={stickerFlowInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <ArrowRight size={24} />
            </motion.div>

            {/* Step 3: Cross Theme Relationships */}
            <motion.div
              className="tmf-analysis-step"
              initial={{ opacity: 0, x: -30 }}
              animate={stickerFlowInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="tmf-analysis-step-number">03</div>
              <div className="tmf-analysis-step-content">
                <div className="tmf-analysis-step-icon">
                  <Network size={24} />
                </div>
                <h4 className="tmf-analysis-step-title">Cross Theme Relationships</h4>
                <p className="tmf-analysis-step-description">
                  Map connections and patterns across identified themes
                </p>
              </div>
            </motion.div>

          </div>

          <div className="tmf-analysis-note">
            <Lightbulb size={20} />
            <p>
              This collaborative thematic analysis ensured patterns emerged through rigorous examination
              of both data sources, with interpretations grounded in participant voices rather than researcher assumptions.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AnalysisSection;
